import { useEffect, useState } from "react";
import Shell from "@/components/app/Shell";
import api, { formatApiError, API } from "@/lib/api";
import { Flag, Upload, Camera, Users, Ruler, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";

const KINDS = [
  { k: "tongue_tie", name: "Tongue Tie", desc: "Restricted tongue movement from a tight lingual frenulum." },
  { k: "lip_tie", name: "Lip Tie", desc: "Tight upper-lip frenulum that limits lip flexibility." },
  { k: "palate_width", name: "Palate Width", desc: "Covers jaw/palate narrowness and related airway space concerns — how much room there is for the tongue and for airflow. Assessed via intermolar width or provider imaging (e.g. CBCT scans)." },
  { k: "tonsils_adenoids", name: "Tonsils / Adenoids", desc: "Enlarged tonsil or adenoid tissue that may obstruct the airway." },
];
const STATUSES = ["none", "flagged", "referred", "cleared"];
const STATUS_LABELS = { none: "No concern", flagged: "Flagged", referred: "Referred", cleared: "Cleared" };

function PalateMeasurements({ child, existing, onSaved }) {
  const [mm, setMm] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const measurements = (existing?.measurements || []).slice().sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  const expected = child.age ? child.age + 24 : null;
  const isAdult = !!child.is_self && !child.age;

  const bandForAdult = (m) => {
    if (m < 32) return { label: "severe underdevelopment", tone: "text-mauve" };
    if (m < 34) return { label: "moderate", tone: "text-mauve" };
    if (m < 36) return { label: "mild", tone: "text-ink-mid" };
    if (m < 38) return { label: "average", tone: "text-ink-dark" };
    if (m <= 42) return { label: "above average", tone: "text-navy" };
    return { label: "above average", tone: "text-navy" };
  };

  const save = async () => {
    const val = parseFloat(mm);
    if (!val || val < 15 || val > 60) { setErr("Enter a value in millimeters (15–60)."); return; }
    setSaving(true); setErr("");
    try {
      await api.post("/roadblocks/palate-measurement", { child_id: child.id, mm: val });
      setMm("");
      onSaved && onSaved();
    } catch (e) { setErr(formatApiError(e)); } finally { setSaving(false); }
  };

  const remove = async (at) => {
    setSaving(true);
    try {
      await api.delete(`/roadblocks/palate-measurement?child_id=${child.id}&at=${encodeURIComponent(at)}`);
      onSaved && onSaved();
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-5 pt-4 border-t border-edge/70" data-testid="rb-palate-measurements">
      <div className="text-xs uppercase tracking-widest text-ink-soft font-semibold mb-1 flex items-center gap-1.5">
        <Ruler size={12} /> Intermolar width history
      </div>
      <div className="text-xs text-ink-mid mb-3">
        Measure with the red wax strip and log the number in mm.
        {expected !== null && <> Expected for age {child.age}: <span className="font-semibold text-navy">{expected}mm</span>.</>}
        {isAdult && <> Adult ranges — below 32mm severe · 32–34 moderate · 34–36 mild · 36–38 average · 38–42 above average.</>}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <input
          type="number" step="0.5" min="15" max="60"
          data-testid="rb-palate-mm"
          value={mm} onChange={(e) => setMm(e.target.value)}
          placeholder="e.g. 30"
          className="w-24 px-3 py-2 rounded-xl border border-edge bg-cream text-sm text-ink-dark"
        />
        <span className="text-xs text-ink-soft">mm</span>
        <button
          data-testid="rb-palate-log"
          onClick={save} disabled={saving}
          className="px-3 py-1.5 rounded-full bg-navy text-warm-white text-xs font-semibold hover:bg-navy-deep disabled:opacity-50"
        >
          Log measurement
        </button>
      </div>
      {err && <div className="mt-2 text-xs text-red-700">{err}</div>}
      {measurements.length > 0 && (
        <div className="mt-3 space-y-1.5" data-testid="rb-palate-history">
          {measurements.map((m) => {
            const gap = expected !== null ? (expected - m.mm).toFixed(1) : null;
            const gapTone = gap === null ? "" : (Math.abs(gap) <= 2 ? "text-navy" : parseFloat(gap) > 2 ? "text-mauve" : "text-ink-mid");
            const adultBand = isAdult ? bandForAdult(m.mm) : null;
            const d = new Date(m.at);
            return (
              <div key={m.at} className="flex items-center justify-between gap-2 rounded-xl border border-edge bg-cream px-3 py-2" data-testid={`rb-palate-entry-${m.at}`}>
                <div className="text-sm text-ink-dark">
                  <span className="font-semibold">{m.mm}mm</span>
                  {expected !== null && (
                    <span className={`ml-2 text-xs ${gapTone}`}>
                      · expected {expected}mm {parseFloat(gap) > 0 ? `(${gap}mm below)` : parseFloat(gap) < 0 ? `(${Math.abs(gap)}mm above)` : "(on target)"}
                    </span>
                  )}
                  {adultBand && <span className={`ml-2 text-xs ${adultBand.tone}`}>· {adultBand.label}</span>}
                  <span className="ml-2 text-xs text-ink-soft">· {d.toLocaleDateString()}</span>
                </div>
                <button onClick={() => remove(m.at)} disabled={saving} className="text-ink-soft hover:text-mauve" data-testid={`rb-palate-remove-${m.at}`}>
                  <Trash2 size={13} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RoadblockCard({ child, kind, existing, onSaved }) {
  const [status, setStatus] = useState(existing?.status || "none");
  const [note, setNote] = useState(existing?.note || "");
  const [severity, setSeverity] = useState(existing?.severity ?? null);
  const [photoPath, setPhotoPath] = useState(existing?.photo_path || null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const isTonsils = kind.k === "tonsils_adenoids";
  const isPalate = kind.k === "palate_width";
  const isTongueTie = kind.k === "tongue_tie";
  const streak = Math.min(3, Math.max(0, existing?.tongue_tie_streak || 0));
  const supportsPhoto = isTonsils || isPalate;

  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    setSaving(true);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoPath(data.path);
    } catch (e) { setErr(formatApiError(e)); } finally { setSaving(false); }
  };

  const save = async () => {
    setSaving(true); setErr("");
    try {
      await api.post("/roadblocks", {
        child_id: child.id, kind: kind.k, status, note,
        severity: isTonsils ? severity : null,
        photo_path: photoPath,
      });
      onSaved && onSaved();
    } catch (e) { setErr(formatApiError(e)); } finally { setSaving(false); }
  };

  const token = localStorage.getItem("btt_token");

  return (
    <div className="bg-warm-white border border-edge rounded-2xl p-5">
      <div className="flex items-center justify-between">
        <div className="font-display font-bold text-navy text-lg">{kind.name}</div>
        <Flag size={16} className="text-mauve" />
      </div>
      {kind.desc && <div className="mt-1 text-xs text-ink-mid leading-relaxed" data-testid={`rb-desc-${kind.k}`}>{kind.desc}</div>}
      {isTongueTie && (
        <div className="mt-3 rounded-xl bg-cream border border-edge px-3 py-2.5" data-testid="rb-tongue-tie-streak">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-ink-soft font-semibold">Check-in streak</div>
              <div className="text-xs text-ink-mid mt-0.5">
                {streak === 0 && "No tongue-tie signs flagged in recent check-ins."}
                {streak === 1 && "Flagged in the last check-in."}
                {streak === 2 && "Flagged 2 check-ins in a row."}
                {streak >= 3 && <span className="text-mauve font-semibold">Flagged 3 check-ins in a row — consider a consultation.</span>}
              </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0" data-testid="rb-tongue-tie-flags">
              {[1, 2, 3].map((i) => (
                <div key={i} className={`w-7 h-7 rounded-full flex items-center justify-center border-2 transition ${i <= streak ? (streak >= 3 ? "bg-mauve border-mauve text-warm-white" : "bg-gold border-gold text-ink-dark") : "border-edge text-ink-soft/60"}`} data-testid={`rb-tongue-tie-flag-${i}${i <= streak ? "-filled" : "-empty"}`}>
                  <Flag size={12} strokeWidth={2.5} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="mt-3 flex gap-2 flex-wrap">
        {STATUSES.map((s) => (
          <button key={s} data-testid={`rb-${kind.k}-${s}`} onClick={() => setStatus(s)} className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${status === s ? (s === "none" ? "bg-cream text-ink-mid border-edge" : "bg-navy text-warm-white border-navy") : "border-edge text-ink-mid hover:border-navy"}`}>{STATUS_LABELS[s] || s}</button>
        ))}
      </div>
      {isTonsils && (
        <div className="mt-4">
          <div className="text-xs uppercase tracking-widest text-ink-soft font-semibold mb-2">Grade (0-4)</div>
          <div className="flex gap-2">
            {[0, 1, 2, 3, 4].map((g) => (
              <button key={g} data-testid={`rb-tonsils-grade-${g}`} onClick={() => setSeverity(g)} className={`w-10 h-10 rounded-full font-bold text-sm border ${severity === g ? "bg-gold border-gold text-ink-dark" : "border-edge text-ink-mid"}`}>{g}</button>
            ))}
          </div>
          <div className="text-xs text-ink-soft mt-1">0 = clear · 4 = touching / kissing tonsils</div>
          <div className="mt-4">
            <label className="text-xs uppercase tracking-widest text-ink-soft font-semibold">Photo (optional)</label>
            {photoPath && <div className="mt-2 rounded-xl overflow-hidden border border-edge"><img alt="tonsils" src={`${API}/files/${photoPath}?auth=${token}`} className="max-h-48" /></div>}
            <label className="mt-2 inline-flex items-center gap-2 text-sm text-navy cursor-pointer">
              <Camera size={14} /> <span className="underline">{photoPath ? "Replace photo" : "Upload photo"}</span>
              <input type="file" accept="image/*" data-testid={`rb-tonsils-photo`} className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
            </label>
          </div>
          {Array.isArray(existing?.photo_history) && existing.photo_history.length > 0 && (
            <div className="mt-5 pt-4 border-t border-edge/70" data-testid="rb-tonsils-photo-history">
              <div className="text-xs uppercase tracking-widest text-ink-soft font-semibold mb-2">Photo history · session-by-session</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {existing.photo_history
                  .slice()
                  .sort((a, b) => (a.session_number || 0) - (b.session_number || 0))
                  .map((h, i) => (
                    <div key={`${h.at}-${i}`} className="rounded-xl border border-edge bg-cream overflow-hidden" data-testid={`rb-tonsils-history-${h.session_number}`}>
                      {h.photo_path ? (
                        <img src={`${API}/files/${h.photo_path}?auth=${token}`} alt={`Session ${h.session_number}`} className="w-full h-24 object-cover" />
                      ) : (
                        <div className="w-full h-24 flex items-center justify-center text-[10px] text-ink-soft bg-warm-white">No photo</div>
                      )}
                      <div className="px-2 py-1.5">
                        <div className="text-xs font-bold text-navy">Session {h.session_number}</div>
                        <div className="text-[10px] text-ink-mid">
                          Grade {h.severity ?? "—"} · {new Date(h.at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}
      {isPalate && (
        <div className="mt-4">
          <label className="text-xs uppercase tracking-widest text-ink-soft font-semibold">Provider imaging (CBCT / panorex)</label>
          {photoPath && <div className="mt-2 rounded-xl overflow-hidden border border-edge"><img alt="palate imaging" src={`${API}/files/${photoPath}?auth=${token}`} className="max-h-48" /></div>}
          <label className="mt-2 inline-flex items-center gap-2 text-sm text-navy cursor-pointer">
            <Upload size={14} /> <span className="underline">{photoPath ? "Replace scan" : "Upload CBCT / panorex scan"}</span>
            <input type="file" accept="image/*,application/pdf" data-testid={`rb-palate-photo`} className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
          </label>
          <div className="text-xs text-ink-soft mt-1">Private to your family — helpful when reviewing airway space with a provider.</div>
          <PalateMeasurements child={child} existing={existing} onSaved={onSaved} />
        </div>
      )}
      <textarea placeholder="Notes…" data-testid={`rb-note-${kind.k}`} value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-4 w-full px-3 py-2 rounded-xl border border-edge bg-cream text-sm text-ink-dark" />
      {err && <div className="mt-2 text-xs text-red-700">{err}</div>}
      <button data-testid={`rb-save-${kind.k}`} onClick={save} disabled={saving} className="mt-3 px-4 py-2 rounded-full bg-gold text-ink-dark text-sm font-semibold hover:bg-gold-light disabled:opacity-50">
        {saving ? "Saving…" : "Save"}
      </button>
    </div>
  );
}

export default function Roadblocks() {
  const [children, setChildren] = useState([]);
  const [current, setCurrent] = useState(null);
  const [rbs, setRbs] = useState([]);

  useEffect(() => { api.get("/children").then(({ data }) => { setChildren(data); if (data[0]) setCurrent(data[0]); }); }, []);
  useEffect(() => { if (current) api.get(`/roadblocks/${current.id}`).then((r) => setRbs(r.data)); }, [current]);

  const reload = () => api.get(`/roadblocks/${current.id}`).then((r) => setRbs(r.data));

  if (!current) return <Shell><div className="text-ink-mid">Enroll a child first.</div></Shell>;

  return (
    <Shell>
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-mauve font-semibold">Track</div>
          <h1 className="font-display text-3xl font-bold text-navy mt-1">Structural Roadblocks</h1>
          <p className="text-ink-mid mt-2">Flag, refer, or clear four common structural factors. Notes and photos are private to your family.</p>
        </div>
        <Link to="/providers" data-testid="my-providers-btn" className="inline-flex items-center gap-2 bg-warm-white border border-edge text-navy px-5 py-2.5 rounded-full text-sm font-semibold hover:border-navy">
          <Users size={14} /> My providers
        </Link>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {KINDS.map((k) => <RoadblockCard key={k.k} child={current} kind={k} existing={rbs.find((r) => r.kind === k.k)} onSaved={reload} />)}
      </div>
    </Shell>
  );
}
